import React, { useState, useEffect, useMemo } from 'react';
import { fetchZmanimActions, fetchZmanimShabbatActions, fetchHebrewDateActions } from './actions/fetchZmanimActions';
import templates from './templates';
import './ZmanimWidget.css';
import { ZMANIM_WIDTH, ZMANIM_HEIGHT } from './constants';
import {
  Flame, Moon, Users,
  Hand as HandMetal, Music, Plus, CloudRain, HeartHandshake, BookOpen,
  Star, MoonStar, CalendarDays, ScrollText, BookOpenText, Info,
} from 'lucide-react';
import { DEFAULT_VISIBLE_ZMANIM } from '../../dummies/WidgetSetupWizard/steps/ZmanimVisibilityStep';
import { computeShabbatEntryTime, computeShabbatExitTime } from '../ShabbatWidget/shabbatTimeUtils';
import { useHalachicData } from '../../hooks/useHalachicData';
import { buildHalachicItems } from '../InyaneiHaYomWidget/inyaneiUtils';
import { mapToAnnotations } from './components/inyaneiAnnotations';

const INYANEI_ICON_MAP = {
  HandMetal, Music, Plus, CloudRain, HeartHandshake, BookOpen,
  Star, MoonStar, CalendarDays, ScrollText, Flame, BookOpenText, Info,
};

export { ZMANIM_WIDTH, ZMANIM_HEIGHT };

export const Icons = {
  SunFull: () => <span className="material-symbols-outlined" style={{ fontSize: '60px' }}>wb_sunny</span>,
  SunHorizon: () => <span className="material-symbols-outlined" style={{ fontSize: '60px' }}>wb_twilight</span>,
  Moon: () => <span className="material-symbols-outlined" style={{ fontSize: '60px' }}>dark_mode</span>,
  Torah: () => <span className="material-symbols-outlined" style={{ fontSize: '55px' }}>menu_book</span>,
  Pray: () => <span className="material-symbols-outlined" style={{ fontSize: '55px' }}>self_improvement</span>,
  Candle: () => <span className="material-symbols-outlined" style={{ fontSize: '60px' }}>candle</span>,
  Custom: () => <span className="material-symbols-outlined" style={{ fontSize: '55px' }}>group</span>
};

const ALL_ZMANIM_DEFINITIONS = [
  { key: 'alotHaShachar',           label: 'עלות השחר',                          icon: <Icons.SunHorizon /> },
  { key: 'misheyakir',              label: 'משיכיר — זמן טלית/תפילין',           icon: <Icons.SunHorizon /> },
  { key: 'misheyakirMachmir',       label: 'משיכיר מחמיר',                        icon: <Icons.SunHorizon /> },
  { key: 'sunrise',                 label: 'הנץ החמה',                           icon: <Icons.SunFull />, isGold: true },
  { key: 'sofZmanShmaMGA',          label: 'סו"ז ק"ש מג"א (90 במע׳)',            icon: <Icons.Torah /> },
  { key: 'sofZmanShmaMGA16Point1',  label: 'סו"ז ק"ש מג"א (72 במע׳)',            icon: <Icons.Torah /> },
  { key: 'sofZmanShma',             label: 'סוף זמן קריאת שמע',                  icon: <Icons.Torah />, bold: true },
  { key: 'sofZmanTfillaMGA',        label: 'סו"ז תפילה מג"א (90 במע׳)',          icon: <Icons.Pray /> },
  { key: 'sofZmanTfillaMGA16Point1',label: 'סו"ז תפילה מג"א (72 במע׳)',          icon: <Icons.Pray /> },
  { key: 'sofZmanTfilla',           label: 'סוף זמן תפילה',                      icon: <Icons.Pray />, bold: true },
  { key: 'chatzot',                 label: 'חצות היום',                          icon: <Icons.SunFull />, isGold: true },
  { key: 'minchaGedola',            label: 'מנחה גדולה — גר"א',                  icon: <Icons.Candle /> },
  { key: 'minchaGedolaMGA',         label: 'מנחה גדולה — מג"א',                  icon: <Icons.Candle /> },
  { key: 'minchaKetana',            label: 'מנחה קטנה — גר"א',                   icon: <Icons.Candle /> },
  { key: 'minchaKetanaMGA',         label: 'מנחה קטנה — מג"א',                   icon: <Icons.Candle /> },
  { key: 'plagHaMincha',            label: 'פלג המנחה',                          icon: <Icons.Candle /> },
  { key: 'sunset',                  label: 'שקיעה',                              icon: <Icons.SunHorizon />, isGold: true },
  { key: 'beinHaShmashos',          label: 'בין השמשות',                          icon: <Icons.SunHorizon /> },
  { key: 'tzeit85deg',              label: 'צאת הכוכבים',                        icon: <Icons.Moon />, bold: true },
  { key: 'tzeit7083deg',            label: 'צאת כוכבים (ר"ת 7°)',                icon: <Icons.Moon /> },
  { key: 'tzeit42min',              label: "צאת כוכבים 42 דק'",                  icon: <Icons.Moon /> },
  { key: 'tzeit50min',              label: "צאת כוכבים 50 דק'",                  icon: <Icons.Moon /> },
  { key: 'tzeit72min',              label: "צאת כוכבים 72 דק'",                  icon: <Icons.Moon /> },
  { key: 'chatzotNight',            label: 'חצות הלילה',                         icon: <Icons.Moon /> },
  // ערב פסח (יחושבו ב-apiService.js — קיימים גם בשאר הימים אך רלוונטיים הלכתית רק בי"ד ניסן)
  { key: 'sofZmanAchilatChametz',    label: 'סוף זמן אכילת חמץ — גר"א',          icon: <Icons.Torah /> },
  { key: 'sofZmanBiurChametz',       label: 'סוף זמן ביעור חמץ — גר"א',          icon: <Icons.Torah /> },
  { key: 'sofZmanAchilatChametzMGA', label: 'סוף זמן אכילת חמץ — מג"א (72 במע׳)',icon: <Icons.Torah /> },
  { key: 'sofZmanBiurChametzMGA',    label: 'סוף זמן ביעור חמץ — מג"א (72 במע׳)',icon: <Icons.Torah /> },
];

export const ZmanimWidget = ({ zmanim, shabbat, hebrewDate, loading, error, customTimes = [], shabbatCustomTimes = [], template = 'Timeline', title = 'סדר היום', showTitle = true, scale = 1, dispatch, location, shabbatColor = '#7c3aed', visibleZmanim = null, shabbatEntryZman = null, shabbatExitZman = null, showInyaneiHayom = false, inyaneiDisplayMode = 'section', inyaneiCategories = null, inyaneiEdah = 'ashkenaz' }) => {
  const [now, setNow] = useState(new Date());

  // ─── ענייני היום — Halachic data integration ───────────────────────────────
  const inyaneiEngineConfig = useMemo(() => ({
    location: {
      lat: location?.lat,
      lng: location?.lng,
      timezone: location?.timezone || location?.tzid || 'Asia/Jerusalem',
    },
    isIsrael: true,
    edah: inyaneiEdah === 'mizrahi' ? 'mizrahi' : inyaneiEdah === 'sephardic' ? 'sephardic' : 'ashkenaz',
  }), [location?.lat, location?.lng, location?.timezone, location?.tzid, inyaneiEdah]);

  const { today: halachicToday } = useHalachicData(inyaneiEngineConfig, { autoFetch: showInyaneiHayom });

  const DEFAULT_INYANEI_SETTINGS = {
    showTachanun: true, showHallel: true, showPrayerAdditions: true,
    showSeasonalPrayers: true, showSeasonalTransitions: true, showSelichot: true,
    showWeekdayPrayers: true, showShabbatAdditions: true, showMotzaeiShabbat: true,
    showSeasonalCustoms: true, showLiturgy: true, showLeyning: false,
    showHaftarah: false, showMourningPeriods: true,
  };

  const inyaneiSettings = useMemo(
    () => inyaneiCategories ? { ...DEFAULT_INYANEI_SETTINGS, ...inyaneiCategories } : DEFAULT_INYANEI_SETTINGS,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [inyaneiCategories],
  );

  const inyaneiItemsRaw = useMemo(() => {
    if (!showInyaneiHayom || !halachicToday) return [];
    return buildHalachicItems(halachicToday, inyaneiSettings, inyaneiEdah);
  }, [showInyaneiHayom, halachicToday, inyaneiSettings, inyaneiEdah]);

  // Enrich items with actual icon components for template rendering
  const inyaneiItems = useMemo(() => {
    return inyaneiItemsRaw.map(item => ({
      ...item,
      IconComponent: INYANEI_ICON_MAP[item.icon] || Info,
    }));
  }, [inyaneiItemsRaw]);

  // Annotation items injected into the timeline for 'annotations' / 'combined' modes
  const annotationItems = useMemo(() => {
    if (!showInyaneiHayom || !zmanim || inyaneiItems.length === 0) return [];
    if (inyaneiDisplayMode !== 'annotations' && inyaneiDisplayMode !== 'combined') return [];
    return mapToAnnotations(inyaneiItems, zmanim);
  }, [showInyaneiHayom, zmanim, inyaneiItems, inyaneiDisplayMode]);

  // פונקציה לחישוב זמנים יחסיים
  const calculateRelativeTime = (baseTime, offset, unit, roundTo) => {
    if (!baseTime) return '--:--';

    try {
      let timeStr = baseTime;

      // אם זה כבר בפורמט HH:MM
      if (baseTime.match(/^\d{2}:\d{2}$/)) {
        timeStr = baseTime;
      }
      // אם זה תאריך מלא
      else if (baseTime.includes('T')) {
        const date = new Date(baseTime);
        timeStr = date.toTimeString().slice(0, 5);
      }
      else {
        const date = new Date(baseTime);
        if (!isNaN(date.getTime())) {
          timeStr = date.toTimeString().slice(0, 5);
        } else {
          return '--:--';
        }
      }

      const [hours, minutes] = timeStr.split(':').map(Number);
      const totalMinutes = hours * 60 + minutes;
      const offsetMinutes = unit === 'hours' ? offset * 60 : offset;
      const newTotalMinutes = totalMinutes + offsetMinutes;

      let finalMinutes = ((newTotalMinutes % (24 * 60)) + (24 * 60)) % (24 * 60);

      // עיגול זמן אם נדרש
      if (roundTo && roundTo > 0) {
        const mins = finalMinutes % 60;
        const roundedMins = Math.floor(mins / roundTo) * roundTo;
        finalMinutes = Math.floor(finalMinutes / 60) * 60 + roundedMins;
      }

      const finalHours = Math.floor(finalMinutes / 60);
      const finalMins = finalMinutes % 60;

      return `${finalHours.toString().padStart(2, '0')}:${finalMins.toString().padStart(2, '0')}`;
    } catch (error) {
      console.error('Error calculating relative time:', error);
      return '--:--';
    }
  };

  // עיבוד זמנים מותאמים אישית
  const processedCustomTimes = customTimes.map(ct => {
    if (ct.type === 'relative' && zmanim) {
      const calculatedTime = calculateRelativeTime(zmanim[ct.baseTime], ct.offset, ct.unit, ct.roundTo);
      return { ...ct, time: calculatedTime };
    }
    return ct;
  });

  // עיבוד זמנים מותאמים אישית של שבת — מעבר ראשון: זמני API
  const buildShabbatBaseTimeMap = (timeWithDay) => {
    const dayData = timeWithDay.day === 'shabbat' ? shabbat.shabbat : shabbat.friday;
    return {
      'dayEntryTime': computeShabbatEntryTime(shabbat, shabbatEntryZman),
      'dayExitTime':  computeShabbatExitTime(shabbat, shabbatExitZman),
      'candleLighting': shabbat.meta?.candleLighting,
      'havdalah':       shabbat.shabbat?.havdalah || shabbat.meta?.havdalah,
      'fridaySunset': shabbat.friday?.sunset,
      'shabbatSunset': shabbat.shabbat?.sunset,
      'fridayTzeit7083': shabbat.friday?.tzeit7083deg,
      'fridayTzeit85': shabbat.friday?.tzeit85deg,
      'fridayTzeit42': shabbat.friday?.tzeit42min,
      'fridayTzeit50': shabbat.friday?.tzeit50min,
      'fridayTzeit72': shabbat.friday?.tzeit72min,
      'shabbatTzeit7083': shabbat.shabbat?.tzeit7083deg,
      'shabbatTzeit85': shabbat.shabbat?.tzeit85deg,
      'shabbatTzeit42': shabbat.shabbat?.tzeit42min,
      'shabbatTzeit50': shabbat.shabbat?.tzeit50min,
      'shabbatTzeit72': shabbat.shabbat?.tzeit72min,
      'sofZmanShma': shabbat.shabbat?.sofZmanShmaGra,
      'sofZmanShmaMga': shabbat.shabbat?.sofZmanShmaMga,
      'shabbatHavdalah': shabbat.shabbat?.havdalah || shabbat.meta?.havdalah || shabbat.havdalah,
      'daySunset':         dayData?.sunset,
      'dayTzeit7083':      dayData?.tzeit7083deg,
      'dayTzeit85':        dayData?.tzeit85deg,
      'dayTzeit42':        dayData?.tzeit42min,
      'dayTzeit50':        dayData?.tzeit50min,
      'dayTzeit72':        dayData?.tzeit72min,
      'dayNetz':           dayData?.netzHachama,
      'dayAlot':           dayData?.alotHaShachar,
      'dayChatzot':        dayData?.chatzot,
      'dayPlagHamincha':   dayData?.plagHamincha,
      'dayMinchaGedola':   dayData?.minchaGedola,
      'dayMinchaKetana':   dayData?.minchaKetana,
      'daySofZmanShma':    shabbat.shabbat?.sofZmanShmaGra,
      'daySofZmanShmaMga': shabbat.shabbat?.sofZmanShmaMga,
      'dayHavdalah':       shabbat.shabbat?.havdalah || shabbat.meta?.havdalah || shabbat.havdalah,
    };
  };

  const shabbatPass1 = shabbatCustomTimes.map(ct => {
    const timeWithDay = { ...ct, day: ct.day || 'friday' };
    if (timeWithDay.type === 'relative' && shabbat && !String(timeWithDay.baseTime || '').startsWith('ct:')) {
      const calculatedTime = calculateRelativeTime(buildShabbatBaseTimeMap(timeWithDay)[timeWithDay.baseTime], timeWithDay.offset, timeWithDay.unit, timeWithDay.roundTo);
      return { ...timeWithDay, time: calculatedTime };
    }
    return timeWithDay;
  });

  // מעבר שני: זמנים יחסיים לזמן מותאם אחר (ct: prefix — מפתח = id)
  const shabbatCtTimeMap = {};
  const shabbatCtLabelMap = {};
  shabbatPass1.forEach(ct => {
    if (ct.id) {
      if (ct.time) shabbatCtTimeMap[ct.id] = ct.time;
      if (ct.label) shabbatCtLabelMap[ct.id] = ct.label;
    }
  });

  const processedShabbatCustomTimes = shabbatPass1.map(ct => {
    if (ct.type === 'relative' && String(ct.baseTime || '').startsWith('ct:')) {
      const refId = ct.baseTime.slice(3);
      const refTime = shabbatCtTimeMap[refId];
      if (!refTime || refTime === '--:--') return { ...ct, time: '--:--' };
      return { ...ct, time: calculateRelativeTime(refTime, ct.offset, ct.unit, ct.roundTo), _baseLabel: shabbatCtLabelMap[refId] || '' };
    }
    return ct;
  });

  useEffect(() => {
    // טען נתונים בטעינה ראשונה
    if (dispatch) {
      dispatch(fetchZmanimActions(location));
      dispatch(fetchHebrewDateActions(now)); // טעינת התאריך העברי
    }

    // בדיקה אם היום שישי או שבת ומשיכת זמני שבת
    const fetchShabbatIfNeeded = () => {
      const currentDate = new Date(); // תמיד הזמן האמיתי, לא מה-closure
      const currentHour = currentDate.getHours();
      const currentMinute = currentDate.getMinutes();
      let dayOfWeek = currentDate.getDay(); // 5 = שישי, 6 = שבת

      // חישוב זמן חצות הלילה (נניח 00:00 אם אין נתונים)
      let chatzotNightHour = 0;
      let chatzotNightMinute = 0;

      // לא יכולים להשתמש ב-zmanim כאן כי הוא עדיין לא נטען
      // אז נשתמש בהנחה שחצות הלילה היא בערך ב-00:00

      // חישוב חצות הלילה + 30 דקות (תחילת היום)
      let startOfDayHour = chatzotNightHour;
      let startOfDayMinute = chatzotNightMinute + 30;
      if (startOfDayMinute >= 60) {
        startOfDayMinute -= 60;
        startOfDayHour += 1;
      }

      // בדיקה אם עברנו את תחילת היום
      const currentTotalMinutes = currentHour * 60 + currentMinute;
      const startOfDayTotalMinutes = startOfDayHour * 60 + startOfDayMinute;

      // אם חצות הלילה היא בלילה (23:xx)
      if (chatzotNightHour >= 23) {
        // תחילת היום היא למחרת (00:xx)
        if (currentHour < 23) {
          // זה היום החדש
        } else {
          // אנחנו בשעה 23:xx
          if (currentTotalMinutes >= startOfDayTotalMinutes) {
            // עברנו את תחילת היום
          } else {
            // עדיין לפני תחילת היום
            dayOfWeek = dayOfWeek === 0 ? 6 : dayOfWeek - 1;
          }
        }
      }
      // אם חצות הלילה היא אחרי חצות (00:xx-22:xx)
      else {
        if (currentTotalMinutes < startOfDayTotalMinutes) {
          // עדיין לפני תחילת היום
          dayOfWeek = dayOfWeek === 0 ? 6 : dayOfWeek - 1;
        }
      }
      if ((dayOfWeek === 5 || dayOfWeek === 6) && location && dispatch) {
        dispatch(fetchZmanimShabbatActions(location));
      } else {
        console.log('❌ Not fetching Shabbat data - dayOfWeek:', dayOfWeek);
      }
    };

    fetchShabbatIfNeeded();

    const clockTimer = setInterval(() => setNow(new Date()), 60_000);

    // רענון בשעה 02:05 (במקום בחצות)
    const scheduleNextRefresh = () => {
      const now = new Date();
      const nextRefresh = new Date(now);

      // אם עכשיו לפני 02:05, הרענון הבא יהיה היום ב-02:05
      // אם עכשיו אחרי 02:05, הרענון הבא יהיה מחר ב-02:05
      if (now.getHours() < 2 || (now.getHours() === 2 && now.getMinutes() < 5)) {
        // עדיין לפני 02:05 היום
        nextRefresh.setHours(2, 5, 0, 0);
      } else {
        // אחרי 02:05, נקבע למחר
        nextRefresh.setDate(nextRefresh.getDate() + 1);
        nextRefresh.setHours(2, 5, 0, 0);
      }

      const msUntilRefresh = nextRefresh - now;

      return setTimeout(() => {
        if (dispatch) {
          dispatch(fetchZmanimActions(location));
          dispatch(fetchHebrewDateActions(new Date())); // רענון התאריך העברי
        }
        fetchShabbatIfNeeded(); // גם לרענן זמני שבת
        scheduleNextRefresh();
      }, msUntilRefresh);
    };

    const timeoutId = scheduleNextRefresh();

    // רענון ביום חמישי ב-12:30 - טוען שבת מראש לפני שישי
    const scheduleThursdayFetch = () => {
      const now = new Date();
      const dayOfWeek = now.getDay(); // 4 = חמישי
      const nextFetch = new Date(now);

      const daysUntilThursday = (4 - dayOfWeek + 7) % 7;

      if (daysUntilThursday === 0) {
        // היום חמישי
        if (now.getHours() < 12 || (now.getHours() === 12 && now.getMinutes() < 30)) {
          nextFetch.setHours(12, 30, 0, 0); // היום ב-12:30
        } else {
          nextFetch.setDate(nextFetch.getDate() + 7); // חמישי הבא
          nextFetch.setHours(12, 30, 0, 0);
        }
      } else {
        nextFetch.setDate(nextFetch.getDate() + daysUntilThursday);
        nextFetch.setHours(12, 30, 0, 0);
      }

      return setTimeout(() => {
        if (dispatch) {
          dispatch(fetchZmanimActions(location));
          dispatch(fetchHebrewDateActions(new Date()));
          dispatch(fetchZmanimShabbatActions(location));
        }
        scheduleThursdayFetch();
      }, nextFetch - now);
    };

    const thursdayTimeoutId = scheduleThursdayFetch();

    return () => {
      clearInterval(clockTimer);
      clearTimeout(timeoutId);
      clearTimeout(thursdayTimeoutId);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, location]);

  const widgetData = useMemo(() => {
    if (!zmanim) return { timelineItems: [], sidebarItems: [], activeItem: null, currentTimeLabel: '' };

    // הגדרות תצוגה
    const MINUTES_IN_VIEW = 1440; // 24 שעות

    const nowTime = now.getTime();
    const startOfDay = new Date(now);
    startOfDay.setHours(0, 0, 0, 0);
    const windowStart = startOfDay.getTime();
    const windowEnd = windowStart + (MINUTES_IN_VIEW * 60 * 1000);
    const pxPerMinute = ZMANIM_HEIGHT / MINUTES_IN_VIEW;
    const today = new Date().toISOString().split('T')[0];

    // כל הזמנים ההלכתיים האפשריים
    const allDefinitions = ALL_ZMANIM_DEFINITIONS;

    // סינון לפי visibleZmanim — אם לא הוגדר, מציג את ברירת המחדל
    const activeKeys = new Set(Array.isArray(visibleZmanim) ? visibleZmanim : DEFAULT_VISIBLE_ZMANIM);

    // זמני חמץ — מוצגים רק ביום י"ד ניסן (hm=1 בהסבר Hebcal: ניסן=חודש 1)
    const CHAMETZ_KEYS = new Set(['sofZmanAchilatChametz', 'sofZmanBiurChametz', 'sofZmanAchilatChametzMGA', 'sofZmanBiurChametzMGA']);
    const is14Nisan = hebrewDate?.hd === 14 && hebrewDate?.hm === 1;

    const definitions = allDefinitions.filter(d => {
      if (!activeKeys.has(d.key)) return false;
      if (CHAMETZ_KEYS.has(d.key) && !is14Nisan) return false;
      return true;
    });


    // יצירת zmanimMap עבור הטמפלייט - הזמנים כבר בפורמט HH:MM
    const zmanimMap = {};
    definitions.forEach(def => {
      const timeStr = zmanim[def.key];
      if (timeStr) {
        zmanimMap[def.key] = timeStr; // כבר בפורמט נכון!
      }
      // הוספת זמני מחר
      const tomorrowTimeStr = zmanim[`${def.key}_tomorrow`];
      if (tomorrowTimeStr) {
        zmanimMap[`${def.key}_tomorrow`] = tomorrowTimeStr;
      }
    });

    const customDefinitions = processedCustomTimes
      .filter(ct => ct.label && ct.time)
      .map(ct => {
        const hoursAhead = ct.hoursAhead || 0;
        const itemDate = new Date(today);
        itemDate.setDate(itemDate.getDate() + Math.floor(hoursAhead / 24));
        const itemDateStr = itemDate.toISOString().split('T')[0];
        return {
          key: `custom-${ct.label}-${hoursAhead}`,
          label: ct.label,
          icon: <Icons.Custom />,
          timeStr: `${itemDateStr}T${ct.time}:00`,
          isCustom: true,
          accentColor: ct.accentColor,
          hoursAhead
        };
      });

    // פונקציה להמרת זמן לפורמט HH:MM
    const formatTimeToHHMM = (timeStr) => {
      if (!timeStr) return null;

      // אם זה כבר בפורמט HH:MM
      if (timeStr.match(/^\d{2}:\d{2}$/)) {
        return timeStr;
      }

      // אם זה תאריך מלא (ISO או כל פורמט אחר)
      try {
        const date = new Date(timeStr);
        if (!isNaN(date.getTime())) {
          const hours = date.getHours().toString().padStart(2, '0');
          const minutes = date.getMinutes().toString().padStart(2, '0');
          return `${hours}:${minutes}`;
        }
      } catch (e) {
        console.error('Error parsing time:', e);
      }

      return null;
    };

    // בדיקת יום השבוע - יום מתחיל חצי שעה אחרי חצות הלילה
    const currentHour = now.getHours();
    const currentMinute = now.getMinutes();
    let dayOfWeek = now.getDay(); // 5 = שישי, 6 = שבת

    // חישוב זמן חצות הלילה
    let chatzotNightHour = 0;
    let chatzotNightMinute = 0;

    if (zmanim?.chatzotNight) {
      const [h, m] = zmanim.chatzotNight.split(':').map(Number);
      chatzotNightHour = h;
      chatzotNightMinute = m;
    }

    // חישוב חצות הלילה + 30 דקות (תחילת היום)
    let startOfDayHour = chatzotNightHour;
    let startOfDayMinute = chatzotNightMinute + 30;
    if (startOfDayMinute >= 60) {
      startOfDayMinute -= 60;
      startOfDayHour += 1;
    }
    if (startOfDayHour >= 24) {
      startOfDayHour -= 24;
    }

    // בדיקה אם עברנו את תחילת היום
    const currentTotalMinutes = currentHour * 60 + currentMinute;
    const startOfDayTotalMinutes = startOfDayHour * 60 + startOfDayMinute;

    // אם חצות הלילה היא בלילה (23:xx)
    if (chatzotNightHour >= 23) {
      // תחילת היום היא למחרת (00:xx)
      // אם אנחנו אחרי חצות (00:00-22:59), עברנו את תחילת היום
      if (currentHour < 23) {
        // זה היום החדש - לא משנים את dayOfWeek
      } else {
        // אנחנו בשעה 23:xx
        if (currentTotalMinutes >= startOfDayTotalMinutes) {
          // עברנו את תחילת היום - זה היום החדש
        } else {
          // עדיין לפני תחילת היום - זה היום הקודם
          dayOfWeek = dayOfWeek === 0 ? 6 : dayOfWeek - 1;
        }
      }
    }
    // אם חצות הלילה היא אחרי חצות (00:xx-22:xx)
    else {
      // תחילת היום היא באותו יום (00:xx-23:xx)
      if (currentTotalMinutes < startOfDayTotalMinutes) {
        // עדיין לפני תחילת היום - זה היום הקודם
        dayOfWeek = dayOfWeek === 0 ? 6 : dayOfWeek - 1;
      }
    }

    const isShabbat = dayOfWeek === 5 || dayOfWeek === 6;

    // יצירת customScheduleItems עבור הטמפלייט
    const customScheduleItems = [];

    const isFriday = dayOfWeek === 5;


    // זמני מועד (isMoad) — מוצגים תמיד, גם בשבת וגם בערב שבת ללא סינון חצות
    processedCustomTimes
      .filter(ct => ct.isMoad && ct.label && ct.time && !(ct.hoursAhead > 0))
      .forEach(ct => {
        const finalType = (!ct.type || ct.type === 'undefined' || ct.type === 'relative' || ct.type === 'fixed' || ct.type === 'highlighted')
          ? 'highlighted'
          : ct.type;
        customScheduleItems.push({
          time: ct.time,
          title: ct.label,
          subtitle: ct.subtitle || '',
          type: finalType,
          accentColor: ct.accentColor,
          hoursAhead: ct.hoursAhead,
          icon: ct.icon || Users
        });
      });

    // זמני תפילה רגילים — רק בימי חול (לא שבת), ביום שישי רק לפני חצות
    if (!isShabbat || isFriday) {
      processedCustomTimes
        .filter(ct => !ct.isMoad && ct.label && ct.time && !(ct.hoursAhead > 0))
        .forEach(ct => {
          // אם זה יום שישי, בדוק אם התפילה מתוזמנת לפני חצות
          if (isFriday && zmanim?.chatzot) {
            const [prayerHour, prayerMinute] = ct.time.split(':').map(Number);
            const [chatzotHour, chatzotMinute] = zmanim.chatzot.split(':').map(Number);

            // אם התפילה היא בשעות הקטנות של הלילה (00:00-05:59), זה נחשב ליום הבא
            // ולכן לא צריך להציג אותה ביום שישי
            if (prayerHour >= 0 && prayerHour < 6) {
              return;
            }

            // אם התפילה מתוזמנת אחרי חצות היום, דלג עליה
            if (prayerHour > chatzotHour || (prayerHour === chatzotHour && prayerMinute >= chatzotMinute)) {
              return;
            }
          }

          const finalType = (!ct.type || ct.type === 'undefined' || ct.type === 'relative' || ct.type === 'fixed' || ct.type === 'highlighted')
            ? 'highlighted'
            : ct.type;
          customScheduleItems.push({
            time: ct.time,
            title: ct.label,
            subtitle: ct.subtitle || '',
            type: finalType,
            accentColor: ct.accentColor,
            hoursAhead: ct.hoursAhead,
            icon: ct.icon || Users
          });
        });
    }

    // הוספת זמני שבת אם היום שישי או שבת
    if (isShabbat && shabbat) {
      // הוספת כניסת שבת (יום שישי) - מסונכרן לזמן המוגדר ברכיב השבת
      if (dayOfWeek === 5) {
        const candleTime = formatTimeToHHMM(computeShabbatEntryTime(shabbat, shabbatEntryZman));
        if (candleTime) {
          customScheduleItems.push({
            time: candleTime,
            title: 'כניסת שבת',
            subtitle: 'הדלקת נרות',
            type: 'simple', // simple = לא יופיע בסיידבר
            icon: Flame // אייקון להבה מ-Lucide
          });
        }
      }

      // הוספת יציאת שבת (יום שבת) - מסונכרן לזמן המוגדר ברכיב השבת
      if (dayOfWeek === 6) {
        const havdalahTime = formatTimeToHHMM(computeShabbatExitTime(shabbat, shabbatExitZman));
        if (havdalahTime) {
          customScheduleItems.push({
            time: havdalahTime,
            title: 'צאת שבת',
            subtitle: 'הבדלה',
            type: 'simple', // simple = לא יופיע בסיידבר
            icon: Moon // אייקון ירח מ-Lucide
          });
        }
      }

      // הוספת זמנים עצמאיים של שבת (אם יש) - אלה כן יכולים להופיע בסיידבר
      if (processedShabbatCustomTimes && processedShabbatCustomTimes.length > 0) {
        // זמני שבת צריכים offset כשאנחנו ביום שישי (03:00-23:59)
        // משתמשים ב-dayOfWeek המתוקן (לא now.getDay() הגולמי)

        processedShabbatCustomTimes
          .filter(ct => {
            // סינון בסיסי - רק בודקים שיש label ו-time
            if (!ct.label || !ct.time) return false;

            // סינון לפי יום - רק זמנים שמתאימים ליום הנוכחי
            if (dayOfWeek === 5) { // יום שישי
              return ct.day === 'friday' || !ct.day; // אם אין day מוגדר, נחשב כשישי (תאימות לאחור)
            } else if (dayOfWeek === 6) { // יום שבת
              return ct.day === 'shabbat';
            }

            return false;
          })
          .forEach(ct => {
            // כל הזמנים העצמאיים (undefined, relative, fixed, highlighted) → highlighted
            const finalType = (!ct.type || ct.type === 'undefined' || ct.type === 'relative' || ct.type === 'fixed' || ct.type === 'highlighted')
              ? 'highlighted'
              : ct.type;

            const isTomorrow = ct.day === 'shabbat' && dayOfWeek === 5;

            customScheduleItems.push({
              time: ct.time,
              title: ct.label,
              subtitle: ct.subtitle || '',
              type: finalType,
              accentColor: ct.accentColor || shabbatColor,
              icon: ct.icon || Users,
              isTomorrow: isTomorrow // זמני שבת ביום שישי מסומנים כיום הבא
            });
          });

      }
    }

    // כניסת/יציאת המועד מוזרקות דרך Dashboard ב-customTimes עם hoursAhead נכון


    let allItems = [...definitions, ...customDefinitions].map(def => {
      const timeStr = def.isCustom ? def.timeStr : zmanim[def.key];
      if (!timeStr) return null;

      let t = new Date(timeStr).getTime();
      // פריטים עם hoursAhead מפורש — התאריך כבר נכון, אל תחיל תיקון 12 שעות
      if (!def.hoursAhead) {
        if (Math.abs(t - nowTime) > 12 * 60 * 60 * 1000) {
          if (t < nowTime) t += 24 * 60 * 60 * 1000;
          else t -= 24 * 60 * 60 * 1000;
        }
      }

      return {
        ...def,
        realTime: t,
        timeFormatted: new Date(t).toLocaleTimeString('he-IL', { hour: '2-digit', minute: '2-digit', hour12: false })
      };
    }).filter(Boolean).sort((a, b) => a.realTime - b.realTime);

    const timelineItems = [];
    const sidebarItems = [];
    let activeItem = null;

    allItems.forEach(item => {
      const itemTime = new Date(item.realTime);
      const nowHour = now.getHours();
      const nowMinute = now.getMinutes();
      const itemHour = itemTime.getHours();
      const itemMinute = itemTime.getMinutes();

      const isNow = (nowHour === itemHour && nowMinute === itemMinute);
      if (isNow) activeItem = { ...item, isActive: true };

      const isInWindow = item.realTime >= windowStart && item.realTime <= windowEnd;

      if (isInWindow) {
        const minutesDiff = (item.realTime - windowStart) / 1000 / 60;
        const idealTop = minutesDiff * pxPerMinute;
        timelineItems.push({ ...item, idealTop, isActive: isNow });
      } else if (item.isCustom) {
        sidebarItems.push(item);
      }
    });

    // התאמת מיקומים למניעת חפיפה
    const MIN_GAP = 120;
    const HEADER_HEIGHT = 200; // גובה הכותרת

    if (timelineItems.length > 0) {
      timelineItems[0].top = Math.max(timelineItems[0].idealTop, HEADER_HEIGHT);

      for (let i = 1; i < timelineItems.length; i++) {
        const prevTop = timelineItems[i - 1].top;
        const idealTop = timelineItems[i].idealTop;
        timelineItems[i].top = Math.max(idealTop, prevTop + MIN_GAP, HEADER_HEIGHT);
      }
    }

    // חישוב מיקום הקו הנוכחי לפי יחס בין פריטים
    let nowLineTop = ((nowTime - windowStart) / 1000 / 60) * pxPerMinute;

    if (timelineItems.length > 1) {
      // מצא בין אילו פריטים הזמן הנוכחי נמצא
      for (let i = 0; i < timelineItems.length - 1; i++) {
        const currItem = timelineItems[i];
        const nextItem = timelineItems[i + 1];

        if (nowTime >= currItem.realTime && nowTime <= nextItem.realTime) {
          const timeGap = nextItem.realTime - currItem.realTime;
          const pixelGap = nextItem.top - currItem.top;
          const timePassed = nowTime - currItem.realTime;
          nowLineTop = currItem.top + (timePassed / timeGap) * pixelGap;
          break;
        }
      }

      // אם הזמן הנוכחי לפני הפריט הראשון
      if (nowTime < timelineItems[0].realTime) {
        const firstItem = timelineItems[0];
        const timeToFirst = firstItem.realTime - windowStart;
        const timePassed = nowTime - windowStart;
        nowLineTop = (timePassed / timeToFirst) * firstItem.top;
      }

      // אם הזמן הנוכחי אחרי הפריט האחרון
      if (nowTime > timelineItems[timelineItems.length - 1].realTime) {
        const lastItem = timelineItems[timelineItems.length - 1];
        const timeFromLast = windowEnd - lastItem.realTime;
        const timePassed = nowTime - lastItem.realTime;
        const remainingPixels = ZMANIM_HEIGHT - lastItem.top;
        nowLineTop = lastItem.top + (timePassed / timeFromLast) * remainingPixels;
      }
    }

    // מיון הזמנים בסרגל הצדדי לפי שעה ביום (מהמוקדם לאיחר)
    sidebarItems.sort((a, b) => {
      const timeA = new Date(a.realTime);
      const timeB = new Date(b.realTime);
      const minutesA = timeA.getHours() * 60 + timeA.getMinutes();
      const minutesB = timeB.getHours() * 60 + timeB.getMinutes();
      return minutesA - minutesB;
    });

    return {
      zmanimMap,
      customScheduleItems,
      timelineItems,
      sidebarItems,
      activeItem,
      nowLineTop,
      currentTimeLabel: now.toLocaleTimeString('he-IL', { hour: '2-digit', minute: '2-digit', hour12: false })
    };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [zmanim, now, processedCustomTimes, shabbat, processedShabbatCustomTimes, visibleZmanim, shabbatEntryZman, shabbatExitZman]);

  // ─── Inject annotation items into customScheduleItems for annotations/combined modes ──
  const finalWidgetData = useMemo(() => {
    if (annotationItems.length === 0) return widgetData;
    return {
      ...widgetData,
      customScheduleItems: [...(widgetData.customScheduleItems || []), ...annotationItems],
    };
  }, [widgetData, annotationItems]);

  const Template = templates[template];
  if (!Template) return <div>Template not found</div>;

  // ─── Pass inyanei data into templates for integrated rendering ──────────────
  const showInyaneiInTemplate = showInyaneiHayom && inyaneiItems.length > 0;

  return (
    <Template
      {...finalWidgetData}
      title={title}
      showTitle={showTitle}
      hebrewDate={hebrewDate}
      scale={scale}
      inyaneiItems={showInyaneiInTemplate ? inyaneiItems : null}
      inyaneiDisplayMode={showInyaneiInTemplate ? inyaneiDisplayMode : null}
    />
  );
};

export default ZmanimWidget;
